import React, { useCallback, useMemo } from 'react';

import { Checkbox, RadioButton, Tooltip } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { LanguageCode, RegionCode, RegionSufficientLanguage, SupportedLanguageFormData } from 'client/types';
import { getIsYangoRequest, shouldMarkAsProvidedByOtherCompany } from 'client/utils';
import * as languagesKeyset from 'i18n/Language';
import * as regionsKeyset from 'i18n/Region';
import * as smartHomeFormKeyset from 'i18n/SmartHomeForm';

import styles from './SmartHomeRegionsSelect.module.scss';

const tSmartHome = i18n(smartHomeFormKeyset);
const tRegions = i18n(regionsKeyset);
const tLanguages = i18n(languagesKeyset);

interface Props {
    enableAllAvailableRegions: boolean;
    languages: SupportedLanguageFormData[];
    selectedRegions: RegionCode[];
    disabled: boolean;
    regionsWithSufficientLanguages: RegionSufficientLanguage[];
    setEnableAllAvailableRegions: (value: boolean) => void;
    setSelectedRegions: (newRegions: RegionCode[]) => void;
    errors: string[] | undefined;
}

export const SmartHomeRegionsSelect: React.FC<Props> = ({
    disabled,
    regionsWithSufficientLanguages,
    setEnableAllAvailableRegions,
    enableAllAvailableRegions,
    languages,
    selectedRegions,
    setSelectedRegions,
    errors,
}) => {
    const regions: { code: RegionCode; title: string; languages: LanguageCode[] }[] = useMemo(() => {
        return regionsWithSufficientLanguages
            .map((v) => ({ code: v.regionCode, title: tRegions(v.regionCode), languages: v.sufficientLanguageCodes }))
            .sort((regionA, regionB) => regionA.title.localeCompare(regionB.title));
    }, [regionsWithSufficientLanguages]);

    const selectedRegionsSet = useMemo(() => {
        return new Set(selectedRegions);
    }, [selectedRegions]);

    const { availableRegionsSet } = useMemo(() => {
        const availableRegions = new Set<RegionCode>();

        for (const lang of languages) {
            if (lang.languageCode === undefined) continue;

            for (const regionSufficientLanguage of regionsWithSufficientLanguages) {
                if (regionSufficientLanguage.sufficientLanguageCodes.indexOf(lang.languageCode) >= 0) {
                    availableRegions.add(regionSufficientLanguage.regionCode);
                }
            }
        }

        return { availableRegionsSet: availableRegions };
    }, [regionsWithSufficientLanguages, languages]);

    const onEnableAllAvailableRegionsChange = useCallback(
        (value: string) => {
            setEnableAllAvailableRegions(value === 'selectAll');
        },
        [setEnableAllAvailableRegions],
    );

    const changeRegionSelectionCallback = useCallback(
        (region: RegionCode, value: boolean) => () => {
            let newSelectedRegions = [...selectedRegions].filter((v) => value || v !== region);

            if (value) {
                const set = new Set(newSelectedRegions);
                set.add(region);
                newSelectedRegions = Array.from(set.values());
            }

            setSelectedRegions(newSelectedRegions);
        },
        [selectedRegions, setSelectedRegions],
    );

    return (
        <Field
            fieldTitle={tSmartHome('regions-and-countries')}
            error={errors && errors[0]}
            containerClassName={styles.smartHomeRegionsSelect__field}
        >
            <RadioButton
                className={styles.smartHomeRegionsSelect__regionsToggler}
                value={enableAllAvailableRegions ? 'selectAll' : 'manual'}
                onUpdate={onEnableAllAvailableRegionsChange}
                options={[
                    { value: 'selectAll', content: tSmartHome('select-all-regions') },
                    { value: 'manual', content: tSmartHome('manuual-regions-select') },
                ]}
                size="l"
                disabled={disabled}
            />
            <div className={styles.smartHomeRegionsSelect__container}>
                {regions.map((region) => {
                    const checked =
                        availableRegionsSet.has(region.code) &&
                        (enableAllAvailableRegions || selectedRegionsSet.has(region.code));

                    const asterisk = shouldMarkAsProvidedByOtherCompany(region.code) ? '*' : '';
                    const isDisabled = disabled || enableAllAvailableRegions || !availableRegionsSet.has(region.code);

                    return (
                        <Tooltip
                            key={region.code}
                            content={tSmartHome('add-languages-to-select-region', {
                                languages: region.languages.map((v) => tLanguages(v)).join(', '),
                            })}
                            openDelay={100}
                            placement="bottom-start"
                            disabled={availableRegionsSet.has(region.code)}
                        >
                            <div>
                                <Checkbox
                                    size="l"
                                    checked={checked}
                                    disabled={isDisabled}
                                    onUpdate={changeRegionSelectionCallback(region.code, !checked)}
                                >
                                    {region.title + asterisk}
                                </Checkbox>
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
            <div className={styles.smartHomeRegionsSelect__caption}>
                <span>* </span>
                {getIsYangoRequest() ? tSmartHome('service-provider-yandex') : tSmartHome('service-provider-yango')}
            </div>
        </Field>
    );
};
